import { useEffect, useRef, createElement } from 'react'
import { Button } from '@highlight/ui'
import { useActiveAndCompletedTurns, removeCompletedTurn } from '@renderer/chat/store/turn-status'
import { useSidepanelState } from '@renderer/overlay/atoms'
import { useOpenViewInSidepanel } from '@renderer/overlay/hooks/use-open-view-in-sidepanel'
import { ChatCompleteIcon } from '../components/toast/nested/icon'
import { Toast } from '../index'
import { toastManager } from '../toast-store'
import { useToast } from '../use-toast'

type ToastApi = ReturnType<typeof useToast>

function formatChatDuration(ms: number): string {
  const totalSeconds = Math.max(1, Math.round(ms / 1000))
  if (totalSeconds < 60) return `Took ${totalSeconds}s`
  const totalMinutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (totalMinutes < 60) return seconds === 0 ? `Took ${totalMinutes}m` : `Took ${totalMinutes}m ${seconds}s`
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return minutes === 0 ? `Took ${hours}h` : `Took ${hours}h ${minutes}m`
}

export function useChatCompletionToasts(toast: ToastApi) {
  const entries = useActiveAndCompletedTurns()
  const sidepanelState = useSidepanelState()
  const { openViewInSidepanel } = useOpenViewInSidepanel()
  const seenRef = useRef(new Set<string>())

  useEffect(() => {
    for (const entry of entries) {
      if (entry.status !== 'completed') {
        seenRef.current.delete(entry.id)
        continue
      }
      if (seenRef.current.has(entry.id)) continue

      // Don't mark seen until we actually fire the toast. When the sidepanel is
      // open we skip (the chat is visible inline), but the completion must still
      // toast once the sidepanel closes — this effect re-runs on sidepanelState
      // changes, so leaving the entry unseen lets it fire then. Marking seen
      // here would permanently swallow it. HIG-5248.
      if (sidepanelState === 'open') continue

      seenRef.current.add(entry.id)

      const chatId = entry.id
      const toastId = `chat-complete-${chatId}`
      const title = entry.title
      const durationLabel = entry.durationMs != null ? formatChatDuration(entry.durationMs) : null
      const resumeChat = () => {
        toastManager.dismiss(toastId)
        removeCompletedTurn(chatId)
        openViewInSidepanel('chat', { chatId, instantScrollToBottom: true }, { sourceLabel: 'Home' })
      }
      toast.add({
        analytics: { type: 'chat complete' },
        id: toastId,
        icon: createElement(ChatCompleteIcon),
        title,
        styles: { badge: 'bg-transparent border-transparent shadow-none' },
        duration: 8_000,
        onHeaderClick: resumeChat,
        body: (id) => (
          <Toast toastId={id}>
            <Toast.Content>
              <Toast.Footer>
                {durationLabel ? <span>{durationLabel}</span> : <span className="flex-1" />}
                <Button onClick={resumeChat} size="sm" variant="outline">
                  Resume chat
                </Button>
              </Toast.Footer>
            </Toast.Content>
          </Toast>
        ),
      })
    }
  }, [entries, sidepanelState, openViewInSidepanel, toast])
}
